export interface TaskThreadContextPromptInput {
  readonly workspaceName: string | null;
  readonly identityDisplayName: string | null;
  readonly campaignName: string | null;
  readonly campaignDescription: string | null;
  readonly projectName: string;
  readonly projectDescription: string | null;
  readonly task: {
    readonly id: string;
    readonly title: string;
    readonly status: string | null;
    readonly priority: string | null;
    readonly dueDate: string | null;
    readonly description: string | null;
    readonly dependsOn: ReadonlyArray<string>;
  };
  readonly dependencyTitles: ReadonlyArray<string>;
  readonly comments?: ReadonlyArray<{
    readonly authorName: string | null;
    readonly content: string;
    readonly createdAt: string;
  }>;
  readonly attachments?: ReadonlyArray<{
    readonly filename: string;
    /** Null when CMMD could not sign this object — see CmmdAttachment.url. */
    readonly url: string | null;
    readonly mimeType: string | null;
    readonly sizeBytes: number | null;
  }>;
  readonly contextWarnings?: ReadonlyArray<string>;
}

const MAX_CONTEXT_COMMENT_COUNT = 10;
const MAX_CONTEXT_ATTACHMENT_COUNT = 12;
const MAX_CONTEXT_FIELD_LENGTH = 1_200;

function quoteIfPresent(value: string | null): string | null {
  if (!value || value.trim().length === 0) return null;
  return `"${value.trim()}"`;
}

function normalizeNonEmpty(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function truncateForPrompt(value: string, maxLength = MAX_CONTEXT_FIELD_LENGTH): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`;
}

function formatSize(sizeBytes: number | null): string | null {
  if (sizeBytes === null || !Number.isFinite(sizeBytes) || sizeBytes < 0) return null;
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface ResolveTaskThreadStartMetadataInput {
  readonly itemId: string;
  readonly payloadTitle: string;
  readonly payloadCmmdTaskId: string | null;
  readonly cmmdTaskTitleById: ReadonlyMap<string, string>;
}

export interface TaskThreadStartMetadata {
  readonly threadTitle: string;
  readonly cmmdTaskId: string | null;
}

/**
 * Resolve robust start-thread metadata from UI state.
 *
 * Why this exists:
 * - Some flows can infer the CMMD task id from `itemId` (`cmmd:<id>`) even when
 *   payload-level metadata is absent.
 * - Thread titles should prefer the canonical CMMD task title when available so
 *   scoped threads don't inherit long context prompts as titles.
 */
export function resolveTaskThreadStartMetadata(
  input: ResolveTaskThreadStartMetadataInput,
): TaskThreadStartMetadata {
  const inferredCmmdTaskId = input.itemId.startsWith("cmmd:")
    ? normalizeNonEmpty(input.itemId.slice(5))
    : null;
  const cmmdTaskId = normalizeNonEmpty(input.payloadCmmdTaskId) ?? inferredCmmdTaskId;
  const canonicalTaskTitle = cmmdTaskId
    ? normalizeNonEmpty(input.cmmdTaskTitleById.get(cmmdTaskId))
    : null;
  const payloadTitle = normalizeNonEmpty(input.payloadTitle);

  return {
    cmmdTaskId,
    threadTitle: canonicalTaskTitle ?? payloadTitle ?? "New thread",
  };
}

export function buildTaskThreadContextPrompt(input: TaskThreadContextPromptInput): string {
  const owner = input.identityDisplayName ?? "the CMMD team";
  const workspaceLine = input.workspaceName
    ? `Workspace: ${input.workspaceName}`
    : "Workspace: (not provided)";
  const campaignLine = input.campaignName
    ? [
        `Campaign: ${input.campaignName}`,
        input.campaignDescription ? `Goal: ${quoteIfPresent(input.campaignDescription)}` : null,
      ]
        .filter(Boolean)
        .join("\n")
    : "Campaign: (none)";

  const projectLine = [
    `Project: ${input.projectName}`,
    input.projectDescription
      ? `Project context: ${quoteIfPresent(input.projectDescription)}`
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  const taskLines = [
    `Task ID: ${input.task.id}`,
    `Task title: ${input.task.title}`,
    `Status: ${input.task.status ?? "unspecified"}`,
    `Priority: ${input.task.priority ?? "unspecified"}`,
    `Due date: ${input.task.dueDate ?? "none"}`,
    input.task.description ? `Task details: ${quoteIfPresent(input.task.description)}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  const dependencyLines =
    input.dependencyTitles.length > 0
      ? [
          "Dependencies:",
          ...input.dependencyTitles.map(
            (title, index) => `- ${title} (${input.task.dependsOn[index] ?? "n/a"})`,
          ),
        ].join("\n")
      : "Dependencies: none";

  const commentLines =
    input.comments && input.comments.length > 0
      ? [
          "CMMD comments:",
          ...input.comments.slice(0, MAX_CONTEXT_COMMENT_COUNT).map((comment) => {
            const author = normalizeNonEmpty(comment.authorName) ?? "Unknown";
            const createdAt = normalizeNonEmpty(comment.createdAt) ?? "unknown time";
            return `- ${author} (${createdAt}): ${truncateForPrompt(comment.content)}`;
          }),
          input.comments.length > MAX_CONTEXT_COMMENT_COUNT
            ? `- (${input.comments.length - MAX_CONTEXT_COMMENT_COUNT} more comments available in CMMD)`
            : null,
        ]
          .filter(Boolean)
          .join("\n")
      : "CMMD comments: none";

  const attachmentLines =
    input.attachments && input.attachments.length > 0
      ? [
          "CMMD attachments:",
          ...input.attachments.slice(0, MAX_CONTEXT_ATTACHMENT_COUNT).map((attachment) => {
            const details = [
              normalizeNonEmpty(attachment.mimeType),
              formatSize(attachment.sizeBytes),
            ].filter(Boolean);
            const suffix = details.length > 0 ? ` (${details.join(", ")})` : "";
            // Say so in words rather than interpolating a null: an agent handed
            // the literal string "null" as a URL will try to fetch it.
            const location = attachment.url === null ? "download URL unavailable" : attachment.url;
            return `- ${attachment.filename}${suffix}: ${location}`;
          }),
          input.attachments.length > MAX_CONTEXT_ATTACHMENT_COUNT
            ? `- (${input.attachments.length - MAX_CONTEXT_ATTACHMENT_COUNT} more attachments available in CMMD)`
            : null,
        ]
          .filter(Boolean)
          .join("\n")
      : "CMMD attachments: none";

  const warningLines =
    input.contextWarnings && input.contextWarnings.length > 0
      ? ["Context warnings:", ...input.contextWarnings.map((warning) => `- ${warning}`)].join("\n")
      : null;

  return [
    `You are starting a new CMMD execution thread. ${owner} expects this work to be planned and executed end-to-end with high quality.`,
    [workspaceLine, campaignLine, projectLine].join("\n"),
    taskLines,
    dependencyLines,
    commentLines,
    attachmentLines,
    warningLines,
    "Start by writing a short understanding paragraph of the task goal and constraints. Then provide an execution checklist before implementation.",
    "After the checklist, execute the work step-by-step, validate results, and summarize what shipped plus any follow-up risks.",
  ]
    .filter(Boolean)
    .join("\n\n");
}
